// Fitted shapes as wireframes (line segment pairs, packed xyz) for the viewer's line sets. A pole is its
// centreline, rings and four lines along it (rings carried along by a rotation-minimising frame, so
// they don't twist on a bent rope). A surface is iso-lines inside its mask and the mask's outline, on
// both faces when it has a thickness, tied across at the rim.
import { type Rigid, apply } from '../align.ts';
import { curve, value, surf } from './bspline.ts';
import { decodeSet } from '../edit/pidset.ts';
import { type ShapeDoc, type PolePiece, type SurfacePiece, isPolePiece } from './doc.ts';

type V = [number, number, number];
const sub = (a: V, b: V): V => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const dot = (a: V, b: V) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const cross = (a: V, b: V): V => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const unit = (a: V): V => { const l = Math.hypot(...a) || 1; return [a[0] / l, a[1] / l, a[2] / l]; };
const axpy = (s: number, x: V, y: V): V => [y[0] + s * x[0], y[1] + s * x[1], y[2] + s * x[2]];

/**
 * A pole along its centreline at K + 1 even steps of t: points X, tangents T, radii rr, and at(k, θ),
 * the point on the tube at step k and angle θ about it (rings carried by a rotation-minimising frame).
 */
export function poleFrame(pc: PolePiece, K: number) {
  const nc = pc.c.length / 3, X: V[] = [], T: V[] = [], rr: number[] = [];
  for (let k = 0; k <= K; k++) {
    const p: V = [0, 0, 0], d: V = [0, 0, 0];
    curve(pc.c, nc, k / K, p, d);
    X.push(p); T.push(unit(d)); rr.push(value(pc.r, pc.r.length, k / K));
  }
  // rotation-minimising frame by double reflection (Wang et al. 2008)
  const R: V[] = [unit(cross(T[0], Math.abs(T[0][2]) < 0.9 ? [0, 0, 1] : [1, 0, 0]))];
  for (let k = 0; k < K; k++) {
    const v1 = sub(X[k + 1], X[k]), c1 = dot(v1, v1);
    if (c1 < 1e-18) { R.push(R[k]); continue; }
    const rL = axpy((-2 / c1) * dot(v1, R[k]), v1, R[k]), tL = axpy((-2 / c1) * dot(v1, T[k]), v1, T[k]);
    const v2 = sub(T[k + 1], tL), c2 = dot(v2, v2);
    R.push(unit(c2 < 1e-18 ? rL : axpy((-2 / c2) * dot(v2, rL), v2, rL)));
  }
  const at = (k: number, th: number): V => {
    const s = cross(T[k], R[k]), c = Math.cos(th) * rr[k], n = Math.sin(th) * rr[k];
    return [X[k][0] + c * R[k][0] + n * s[0], X[k][1] + c * R[k][1] + n * s[1], X[k][2] + c * R[k][2] + n * s[2]];
  };
  return { X, T, R, rr, at };
}

export function poleLines(pc: PolePiece, M: Rigid, out: number[] = []) {
  const nc = pc.c.length / 3, K = Math.max(24, 8 * nc), { X, rr, at } = poleFrame(pc, K);
  const seg = (a: V, b: V) => { out.push(...apply(M, a), ...apply(M, b)); };
  for (let k = 0; k < K; k++) seg(X[k], X[k + 1]);
  if (!rr.some(r => r > 0)) return out;
  for (let q = 0; q < 4; q++) for (let k = 0; k < K; k++) seg(at(k, (q * Math.PI) / 2), at(k + 1, (q * Math.PI) / 2));
  const rings = Math.max(6, 2 * nc);
  for (let j = 0; j <= rings; j++) {
    const k = Math.round((j * K) / rings);
    for (let i = 0; i < 16; i++) seg(at(k, (i * Math.PI) / 8), at(k, ((i + 1) * Math.PI) / 8));
  }
  return out;
}

/**
 * A surface piece's geometry: its mask (cells of a mw x mh grid), and P(a, b, w), the point at
 * (a, b) ∈ [0, 1]² off the mid-surface by w along its normal (written to nrm when given).
 */
export function surfaceGeom(sp: SurfacePiece) {
  const { nu, nv, h, ab } = sp, la = ab[1] - ab[0], lb = ab[3] - ab[2], N = cross(sp.u as V, sp.v as V);
  const mw = sp.mask.w, mh = sp.mask.h, occ = new Uint8Array(mw * mh);
  for (const c of decodeSet(sp.mask.set, mw * mh)) occ[c] = 1;
  const inMask = (x: number, y: number) => x >= 0 && y >= 0 && x < mw && y < mh && occ[y * mw + x] === 1;
  const cellOf = (a: number, b: number) => [Math.min(mw - 1, Math.floor(a * mw)), Math.min(mh - 1, Math.floor(b * mh))] as [number, number];
  const f = [0, 0, 0], world = (l: number[], o: number) => [0, 1, 2].map(k => o * sp.o[k] + l[0] * sp.u[k] + l[1] * sp.v[k] + l[2] * N[k]) as V;
  const P = (a: number, b: number, w: number, nrm?: V): V => {
    surf(h, nu, nv, a, b, f);
    const n = unit([-f[1] / la, -f[2] / lb, 1]), A = ab[0] + a * la, B = ab[2] + b * lb;
    if (nrm) nrm.splice(0, 3, ...world(n, 0));
    return world([A + w * n[0], B + w * n[1], f[0] + w * n[2]], 1);
  };
  return { la, lb, mw, mh, inMask, cellOf, P };
}

export function surfaceLines(sp: SurfacePiece, M: Rigid, out: number[] = []) {
  const { nu, nv, t } = sp, { mw, mh, inMask, cellOf, P } = surfaceGeom(sp);
  const sheets = t > 0 ? [-t / 2, t / 2] : [0];
  const seg = (a: V, b: V) => { out.push(...apply(M, a), ...apply(M, b)); };
  // iso-lines, about one per control span, kept where both ends are in the mask
  const lines = (n: number, along: (s: number, u: number) => [number, number]) => {
    const steps = 4 * Math.max(nu, nv);
    for (let i = 1; i < n; i++) {
      const s = i / n;
      for (let j = 0; j < steps; j++) {
        const [a0, b0] = along(s, j / steps), [a1, b1] = along(s, (j + 1) / steps);
        if (!inMask(...cellOf(a0, b0)) || !inMask(...cellOf(a1, b1))) continue;
        for (const w of sheets) seg(P(a0, b0, w), P(a1, b1, w));
      }
    }
  };
  lines(nu - 2, (s, u) => [s, u]);
  lines(nv - 2, (s, u) => [u, s]);
  // the outline: cell edges between in and out, tied across every few edges
  let k = 0;
  for (let y = 0; y < mh; y++) for (let x = 0; x < mw; x++) {
    if (!inMask(x, y)) continue;
    const edges: [number, number, number, number, boolean][] = [
      [x, y, x + 1, y, !inMask(x, y - 1)], [x, y + 1, x + 1, y + 1, !inMask(x, y + 1)],
      [x, y, x, y + 1, !inMask(x - 1, y)], [x + 1, y, x + 1, y + 1, !inMask(x + 1, y)],
    ];
    for (const [x0, y0, x1, y1, open] of edges) {
      if (!open) continue;
      for (const w of sheets) seg(P(x0 / mw, y0 / mh, w), P(x1 / mw, y1 / mh, w));
      if (sheets.length > 1 && k++ % 3 === 0) seg(P(x0 / mw, y0 / mh, sheets[0]), P(x0 / mw, y0 / mh, sheets[1]));
    }
  }
  return out;
}

/** A shape's wireframe placed by M (the reference instance's fit). */
export function shapeLines(doc: ShapeDoc, M: Rigid): Float32Array {
  const out: number[] = [];
  for (const q of doc.pieces) if (isPolePiece(q)) poleLines(q, M, out); else surfaceLines(q, M, out);
  return new Float32Array(out);
}
